import { Enemy } from "../Enemy.js";
import { MeshBuilder, StandardMaterial, Color3, Vector3, SceneLoader } from "@babylonjs/core";

/**
 * @class Bombardier
 * ─────────────────────────────────────────────────────────────
 * ARTILLERIE — Lance des bombes en cloche sur la position du joueur.
 *
 * COMPORTEMENT :
 *   Reste à longue distance. Si le joueur approche, il fuit.
 *   Tire des bombes avec une trajectoire parabolique.
 *   Affiche un marqueur visuel (AoE) au sol là où la bombe va atterrir.
 * ─────────────────────────────────────────────────────────────
 */
export class Bombardier extends Enemy {
    constructor(scene, position) {
        super(scene, "Bombardier", position);

        // ─────────────────────────────
        // STATS
        // ─────────────────────────────
        this.hp    = 2;
        this.maxHp = 2;
        this.speed = 0.11;

        // ─────────────────────────────
        // ZONES DE DÉTECTION & KITE
        // ─────────────────────────────
        this.detectionRange = 25; // Détecte de très loin
        this.kiteMin        = 12; // Fuite si le joueur est trop proche
        this.kiteMax        = 20; // Reste à cette distance idéale
        this.state          = 'ROAM';

        // ─────────────────────────────
        // ARMEMENT (BOMBES)
        // ─────────────────────────────
        this.bombCooldown = 60; // Tir assez vite la première fois
        this.bombInterval = 180; // ~3 secondes entre chaque tir
        this.bombs = [];
        this.explosionRadius = 3.5; // Rayon de dégâts de la bombe

        // ✅ _initBase après les stats — hp=2 est déjà défini
        this._initBase(position);
    }

    // ══════════════════════════════════════════════════════════════
    //  MESH & MATÉRIAUX
    // ══════════════════════════════════════════════════════════════

    _createMesh() {
        const hitbox = MeshBuilder.CreateBox("bombardier_hitbox", { size: 1.2 }, this.scene);
        hitbox.isVisible = false;

        const visualGroup = MeshBuilder.CreateBox("bombardier_visual", { size: 0.01 }, this.scene);
        visualGroup.parent = hitbox;
        visualGroup.isVisible = false;
        this.visualGroup = visualGroup;

        // Base: carbon cube
        const baseBox = MeshBuilder.CreateBox("bombardier_base", { width: 1.1, height: 0.6, depth: 1.1 }, this.scene);
        baseBox.parent = visualGroup;
        baseBox.position.y = -0.3;

        // Glowing red sphere in the middle of base
        const core = MeshBuilder.CreateSphere("bombardier_core", { diameter: 0.65 }, this.scene);
        core.parent = visualGroup;
        core.position.y = 0.1;

        // Canon cylinder pointing up-forward
        const canon = MeshBuilder.CreateCylinder("bombardier_canon", {
            diameter: 0.4, height: 1.3
        }, this.scene);
        canon.parent = visualGroup;
        canon.rotation.x = Math.PI / 4;
        canon.position.y = 0.55;
        canon.position.z = 0.4;

        this._baseBox = baseBox;
        this._core = core;
        this._canon = canon;
        return hitbox;
    }

    _applyMaterial() {
        const baseMat = new StandardMaterial("bombardierBaseMat", this.scene);
        baseMat.diffuseColor = new Color3(0.02, 0.02, 0.03);
        baseMat.emissiveColor = new Color3(0.1, 0.0, 0.05); // Very dark purple
        baseMat.specularColor = new Color3(0.2, 0.2, 0.25);
        if (this._baseBox) this._baseBox.material = baseMat;

        const coreMat = new StandardMaterial("bombardierCoreMat", this.scene);
        coreMat.diffuseColor = new Color3(0.05, 0.0, 0.0);
        coreMat.emissiveColor = new Color3(0.8, 0.0, 0.0); // Dark Blood Red
        coreMat.specularColor = new Color3(0.2, 0.0, 0.0);
        if (this._core) this._core.material = coreMat;

        if (this._canon) {
            const canonMat = new StandardMaterial("canonMat", this.scene);
            canonMat.diffuseColor = new Color3(0.01, 0.01, 0.02);
            canonMat.emissiveColor = new Color3(0.3, 0.0, 0.0); // glowing dark red tip
            this._canon.material = canonMat;
        }

        // Matériaux de bombes créés une seule fois et réutilisés à chaque lancer
        this._bombMat = new StandardMaterial("bombardierBombMat", this.scene);
        this._bombMat.emissiveColor = new Color3(1, 0, 0);

        this._markerMat = new StandardMaterial("bombardierMarkerMat", this.scene);
        this._markerMat.emissiveColor = new Color3(1, 0.2, 0);
        this._markerMat.alpha = 0.5;

        // Observer pour flotter
        this._renderObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (this.isDestroyed || !this.mesh) return;
            if (this.visualGroup) {
                this.visualGroup.position.y = Math.sin(Date.now() * 0.002) * 0.08;
            }
        });
    }

    // ══════════════════════════════════════════════════════════════
    //  DÉPLACEMENT
    // ══════════════════════════════════════════════════════════════

    _isValidPosition(targetPosition, entityManager) {
        if (entityManager?.levelManager?.currentRoom) {
            return entityManager.levelManager.currentRoom.isValidPosition(
                targetPosition.x, targetPosition.z
            );
        }
        return true;
    }

    _moveTo(targetPosition, entityManager) {
        if (this._isValidPosition(targetPosition, entityManager)) {
            this.mesh.position.x = targetPosition.x;
            this.mesh.position.z = targetPosition.z;
            this.mesh.position.y = 1;
        }
    }

    activate(position) {
        super.activate(position);
        this.hp = this.maxHp;
        this.bombCooldown = 60;
        this._clearBombs();
    }

    deactivate() {
        if (this.isDestroyed) return;
        super.deactivate();
        this._clearBombs();
    }

    _clearBombs() {
        if (this.bombs) {
            this.bombs.forEach(bomb => {
                if (bomb.metadata?.marker) bomb.metadata.marker.dispose();
                bomb.dispose();
            });
            this.bombs = [];
        }
    }

    // ══════════════════════════════════════════════════════════════
    //  BOMBES ET EXPLOSIONS
    // ══════════════════════════════════════════════════════════════

    _throwBomb(targetPos) {
        // Création de la bombe
        const bomb = MeshBuilder.CreateSphere("bomb", { diameter: 0.6 }, this.scene);
        bomb.position = this.mesh.position.clone();
        bomb.position.y = 1.5;

        bomb.material = this._bombMat;

        // Création du marqueur au sol (AoE)
        const marker = MeshBuilder.CreateTorus("bomb_marker", {
            diameter: this.explosionRadius * 2, thickness: 0.1, tessellation: 32
        }, this.scene);
        marker.position = new Vector3(targetPos.x, 0.1, targetPos.z);
        marker.material = this._markerMat;

        bomb.metadata = {
            startPos: bomb.position.clone(),
            targetPos: new Vector3(targetPos.x, 0, targetPos.z), // Atterrissage au sol
            progress: 0,
            speed: 0.015, // Vitesse de vol (1 = arrivé)
            arcHeight: 5, // Hauteur max de la parabole
            marker: marker,
            isExploding: false
        };

        this.bombs.push(bomb);
    }

    _updateBombs(player) {
        for (let i = this.bombs.length - 1; i >= 0; i--) {
            const bomb = this.bombs[i];
            const meta = bomb.metadata;

            if (meta.isExploding) continue;

            meta.progress += meta.speed;

            if (meta.progress >= 1) {
                // EXPLOSION !
                this._explodeBomb(bomb, meta, player);
                this.bombs.splice(i, 1);
            } else {
                // Interpolation linéaire pour X et Z
                bomb.position = Vector3.Lerp(meta.startPos, meta.targetPos, meta.progress);

                // Ajout de la parabole pour Y (cloche)
                // Math.sin d'un angle de 0 à PI donne une belle courbe de 0 -> 1 -> 0
                bomb.position.y = Vector3.Lerp(meta.startPos, meta.targetPos, meta.progress).y +
                    Math.sin(meta.progress * Math.PI) * meta.arcHeight;

                // Clignotement du marqueur
                meta.marker.material.alpha = 0.3 + (Math.sin(meta.progress * Math.PI * 8) * 0.2);
            }
        }
    }

    _explodeBomb(bomb, meta, player) {
        // Dégâts si le joueur est dans le rayon
        if (player?.mesh) {
            const dist = Vector3.Distance(meta.targetPos, player.mesh.position);
            if (dist <= this.explosionRadius) {
                player.takeDamage();
            }
        }

        // Explosion VFX
        if (this.context?.vfx) this.context.vfx.bombExplosion(meta.targetPos);

        // Nettoyage
        bomb.dispose();
        meta.marker.dispose();
    }

    // ══════════════════════════════════════════════════════════════
    //  IA PRINCIPALE
    // ══════════════════════════════════════════════════════════════

    think(player, entityManager, aiCollector) {
        if (this.isDestroyed || !player?.mesh) return;

        const toPlayer = player.mesh.position.subtract(this.mesh.position);
        const distance = toPlayer.length();
        if (distance < 0.001) return;

        const towardDir = toPlayer.normalize();
        const awayDir   = towardDir.scale(-1);

        // Comportement de distance (Kite basique)
        if (distance < this.kiteMin) {
            // Fuit si trop proche
            this._moveTo(this.mesh.position.add(awayDir.scale(this.speed * 1.2)), entityManager);
        } else if (distance > this.kiteMax && distance < this.detectionRange) {
            // Avance s'il est trop loin
            this._moveTo(this.mesh.position.add(towardDir.scale(this.speed)), entityManager);
        } else {
            // Strafe très léger sur place
            const strafeDir = new Vector3(-towardDir.z, 0, towardDir.x).scale(this.speed * 0.3);
            this._moveTo(this.mesh.position.add(strafeDir), entityManager);
        }

        // Rotation canon/corps vers le joueur
        this.mesh.rotation.y = Math.atan2(towardDir.x, towardDir.z);

        // Tir de bombes
        if (distance < this.detectionRange) {
            this.bombCooldown--;
            if (this.bombCooldown <= 0) {
                this._throwBomb(player.mesh.position);
                this.bombCooldown = this.bombInterval;
            }
        }

        this._updateBombs(player);
    }

    // ══════════════════════════════════════════════════════════════
    //  NETTOYAGE
    // ══════════════════════════════════════════════════════════════

    dispose() {
        if (this.isDestroyed) return;

        if (this._renderObserver) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
        }

        // On détruit les bombes en vol et leurs marqueurs si l'artilleur meurt
        this.bombs.forEach(bomb => {
            if (bomb.metadata?.marker) bomb.metadata.marker.dispose();
            bomb.dispose();
        });
        this.bombs = [];

        super.dispose();
    }
}